import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { IAppState, TAppActions } from '../rootDuck';
import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { IProduct, IProductFilter } from '../../interfaces/product';
import { deleteProduct, getProducts } from '../../crud/product.crud';
import { getResponseMessage } from '../../utils/utils';
import { ILocation } from '../../interfaces/locations';
import { SortType } from '../../interfaces/productType';

const CLEAR_REQUEST = 'products-catalog/CLEAR_REQUEST';
const FETCH_REQUEST = 'products-catalog/FETCH_REQUEST';
const CHANGE_DATA_FROM_LOCATION = 'products-catalog/CHANGE_DATA_FROM_LOCATION';

const FETCH_SUCCESS = 'products-catalog/FETCH_SUCCESS';
const FETCH_FAIL = 'products-catalog/FETCH_FAIL';
const SET_EMPTY = 'products-catalog/SET_EMPTY';
const DEL_REQUEST = 'products-catalog/DEL_REQUEST';
const DEL_SUCCESS = 'products-catalog/DEL_SUCCESS';
const DEL_FAIL = 'products-catalog/DEL_FAIL';
const CLEAR_DEL = 'products-catalog/CLEAR_DEL';
const SET_FILTER = 'products-catalog/SET_FILTER';
const CLEAR_FILTER = 'products-catalog/CLEAR_FILTER';
const CLEAR_PRODUCTS = 'products-catalog/CLEAR_PRODUCTS';
const SET_VIEWTYPE = 'products-catalog/SET_VIEWTYPE';
const SET_SEARCH = 'products-catalog/SET_SEARCH';
const SET_SEARCH_TEXT = 'products-catalog/SET_SEARCH_TEXT';
const SET_PAGE = 'products-catalog/SET_PAGE';
const SET_CATEGORY_ID_ACTIVE = 'products-catalog/SET_CATEGORY_ID_ACTIVE';
const SET_SEARCHING = 'products-catalog/SET_SEARCHING';

export interface IInitialState {
  page: number;
  per_page: number;
  total: number;
  products: IProduct[] | undefined;
  loading: boolean;
  success: boolean;
  error: string | null;
  delError: string | null;
  delSuccess: boolean;
  isSearching: boolean;
  filter: IProductFilter;
  viewType: 'table' | 'grid' | 'list';
  search: string | undefined;
  searchText: string | undefined;

  sortType: string | undefined;
  categoryIdActive: number | null;
}

const defaultPaginatorProps = {
  page: 1,
  per_page: 10,
  total: 0,
};

const getSavedPerPage = (): number => {
  const saved = localStorage.getItem('productsPerPage');
  if (saved) {
    const parsed = parseInt(saved, 10);
    if (!Number.isNaN(parsed) && [10, 50, 100].includes(parsed)) {
      return parsed;
    }
  }
  return defaultPaginatorProps.per_page;
};

const initialState: IInitialState = {
  ...defaultPaginatorProps,
  per_page: getSavedPerPage(),
  products: undefined,
  loading: false,
  success: false,
  error: null,
  delError: null,
  delSuccess: false,
  isSearching: false,
  search: '',
  searchText: '',
  sortType: '',
  filter: {
    price_from: 0,
    price_to: 0,
    parameters: '',
  },
  viewType:
    (sessionStorage.getItem('viewType') as 'table' | 'grid' | 'list') || null || 'table',
  categoryIdActive: null,
};

export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'products-catalog', whitelist: ['user', 'authToken'] },
  (state = initialState, action: any) => {
    switch (action.type) {
      case SET_EMPTY: {
        return { ...state, products: [] };
      }

      case CLEAR_PRODUCTS: {
        return { ...state, products: undefined, filter: initialState.filter };
      }

      case CLEAR_REQUEST: {
        return { ...state, page: 1 };
      }

      case FETCH_REQUEST: {
        const newPerPage = action.payload.per_page || state.per_page;
        if (action.payload.per_page) {
          localStorage.setItem('productsPerPage', String(action.payload.per_page));
        }
        return {
          ...state,
          products: undefined,
          loading: true,
          success: false,
          error: null,
          search: action.payload.search,
          sortType: action.payload.sort_type || '',
          page: action.payload.page || state.page,
          per_page: newPerPage,
        };
      }

      case CHANGE_DATA_FROM_LOCATION: {
        return {
          ...state,
          products: action.payload.data,
          // page: action.payload.page,
        };
      }

      case FETCH_SUCCESS: {
        if (action.payload.per_page) {
          localStorage.setItem('productsPerPage', String(action.payload.per_page));
        }
        return {
          ...state,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          products: action.payload.data,
          loading: false,
          success: true,
        };
      }

      case SET_CATEGORY_ID_ACTIVE: {
        return {
          ...state,
          categoryIdActive: action.payload,
          page: state.categoryIdActive === action.payload ? state.page : 1,
          // per_page: state.categoryIdActive === action.payload ? state.per_page : 12,
        };
      }

      case SET_FILTER: {
        const filter = {
          ...state.filter,
          ...action.payload,
        };

        return {
          ...state,
          ...defaultPaginatorProps,
          filter,
        };
      }

      case CLEAR_FILTER: {
        return {
          ...state,
          filter: initialState.filter,
        };
      }

      case FETCH_FAIL: {
        return { ...state, loading: false, error: action.payload };
      }

      case DEL_FAIL: {
        return { ...state, delError: action.payload, delSuccess: false };
      }

      case DEL_SUCCESS: {
        return { ...state, delError: null, delSuccess: true };
      }

      case CLEAR_DEL: {
        return { ...state, delError: null, delSuccess: false };
      }

      case SET_VIEWTYPE: {
        sessionStorage.setItem('viewType', action.payload);
        return { ...state, viewType: action.payload };
      }

      case SET_SEARCH: {
        return { ...state, isSearching: action.payload };
      }
      case SET_SEARCHING: {
        return { ...state, search: action.payload };
      }
      case SET_SEARCH_TEXT: {
        return { ...state, searchText: action.payload };
      }

      default:
        return state;
    }
  }
);

export function setProducts(payload: any) {
  return {
    type: CHANGE_DATA_FROM_LOCATION,
    payload,
  };
}

export const actions = {
  clearRequest: () => createAction(CLEAR_REQUEST),
  fetchRequest: (payload: {
    page?: number;
    perPage?: number;
    filter?: IProductFilter;
    search?: string;
    categoryId?: number;
    sort_type?: string;
    locale?: ILocation | null;
    activity?: string;
    sortType?: SortType;
    manufacturer_ids?: string;
    price_from?: number;
    price_to?: number;
  }) => {
    return createAction(FETCH_REQUEST, payload);
  },
  setSearching: (payload: string) => createAction(SET_SEARCHING, payload),
  fetchSuccess: (payload: IServerResponse<IProduct[]>) => createAction(FETCH_SUCCESS, payload),
  fetchFail: (payload: string) => createAction(FETCH_FAIL, payload),
  setEmpty: () => createAction(SET_EMPTY),
  clearProducts: () => createAction(CLEAR_PRODUCTS),
  setFilter: (payload: Partial<IProductFilter>) => createAction(SET_FILTER, payload),
  clearFilter: () => createAction(CLEAR_FILTER),
  delRequest: (payload: {
    id: number | undefined;
    page: number;
    perPage: number;
    filter?: IProductFilter;
    categoryId?: number;
    manufacturer_ids?: string;
    price_from?: number;
    price_to?: number;
  }) => createAction(DEL_REQUEST, payload),
  delFail: (payload: string) => createAction(DEL_FAIL, payload),
  delSuccess: () => createAction(DEL_SUCCESS),
  clearDel: () => createAction(CLEAR_DEL),
  setViewType: (payload: 'table' | 'grid' | 'list') => createAction(SET_VIEWTYPE, payload),
  setSearch: (payload: boolean) => createAction(SET_SEARCH, payload),
  setSearchText: (payload: string) => createAction(SET_SEARCH_TEXT, payload),
  setPage: (payload: { page: number; perPage: number }) => createAction(SET_PAGE, payload),
  setCategoryIdActive: (payload: number) => createAction(SET_CATEGORY_ID_ACTIVE, payload),
};

export type TActions = ActionsUnion<typeof actions>;

export const selectPagePerPage = (state: IAppState) => ({
  page: state.productsCatalog.page,
  perPage: state.productsCatalog.per_page,
  categoryIdActive: state.productsCatalog.categoryIdActive,
  searchText: state.productsCatalog.searchText,
});

function* fetchSaga({
  payload,
}: {
  payload: {
    page?: number;
    perPage?: number;
    filter?: IProductFilter;
    search?: string;
    categoryId?: number;
    locale?: ILocation | null;
    activity?: string;
    sortType?: SortType;
    manufacturer_ids?: string;
    price_from?: number;
    price_to?: number;
  };
}) {
  try {
    const {
      page,
      perPage,
      categoryIdActive,
      search,
    }: { page: number; perPage: number; categoryIdActive: number; search: string } =
      yield select(selectPagePerPage);
    const isCategoryEqual = payload.categoryId === categoryIdActive;
    const isSearchTextEqual = !!payload.search && payload.search === search;
    const categoryPage = isCategoryEqual || isSearchTextEqual ? page : payload.page;
    const categoryPerPage =
      isCategoryEqual || isSearchTextEqual ? perPage : payload.perPage ?? perPage;
    if (typeof payload.categoryId === 'number') {
      yield put(actions.setCategoryIdActive(payload.categoryId));
    }
    const { data }: { data: IServerResponse<IProduct[]> } = yield call(() =>
      payload.page
        ? getProducts({
            ...payload,
          })
        : getProducts({
            ...payload,
            page: categoryPage,
            perPage: categoryPerPage,
          })
    );
    if (
      data.total_pages > 0 &&
      data.total_pages < (payload.page ? payload.page || 1 : categoryPage || 1)
    ) {
      yield put(actions.fetchRequest({ ...payload, page: 1 }));
    } else {
      yield put(actions.fetchSuccess(data));
      yield put(actions.setSearchText(payload.search || ''));
    }
  } catch (e) {
    yield put(actions.fetchFail(getResponseMessage(e)));
  }
}

function* delSaga({ payload }: any) {
  try {
    yield call(() => deleteProduct(payload.id));
    yield put(actions.delSuccess());
    yield put(
      actions.fetchRequest({
        page: payload.page,
        perPage: payload.perPage,
        filter: payload.filter,
        categoryId: payload.categoryId,
        manufacturer_ids: payload.manufacturer_ids,
        price_from: payload.price_from,
        price_to: payload.price_to,
      })
    );
  } catch (e) {
    yield put(actions.delFail(getResponseMessage(e)));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.fetchRequest>>(FETCH_REQUEST, fetchSaga);
  yield takeLatest<ReturnType<typeof actions.delRequest>>(DEL_REQUEST, delSaga);
}
